import { useCallback, useState } from "react";
import type { TFunction } from "i18next";
import { Check, ChevronsUpDown, Loader2, User as UserIcon } from "lucide-react";

import { useUserSearch, type UserSearchItem } from "~/entities/user";
import { useDebouncedSearchInput } from "~/shared/hooks/useDebouncedSearchInput";
import { initials } from "~/shared/lib/initials";
import { nearBottom } from "~/shared/lib/near-bottom";
import { Avatar, AvatarFallback, AvatarImage } from "~/shared/ui/avatar";
import { Button } from "~/shared/ui/button";
import { Input } from "~/shared/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "~/shared/ui/popover";

import type { AttributionCreator } from "./api";
import { creatorLabel } from "./creator-label";

export interface CreatorPickerProps {
    t: TFunction;
    /** Кто числится создателем сейчас — показывается на кнопке, пока не выбран другой. */
    value: AttributionCreator | null;
    onSelect: (user: UserSearchItem) => void;
    disabled?: boolean;
    id?: string;
}

/**
 * Выбор создателя среди всех зарегистрированных пользователей: серверный поиск
 * по подстроке email/имени или точному UUID, страницы догружаются прокруткой.
 */
export function CreatorPicker({ t, value, onSelect, disabled = false, id }: CreatorPickerProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const input = useDebouncedSearchInput(search, setSearch);

    // Список тянем только при открытом пикере — закрытая форма не должна ходить в поиск.
    const query = useUserSearch(search, { enabled: open });
    const users = query.data?.pages.flatMap((page) => page.items) ?? [];

    const handleScroll = useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            if (!query.hasNextPage || query.isFetchingNextPage) return;
            if (nearBottom(event.currentTarget)) void query.fetchNextPage();
        },
        [query],
    );

    const pick = (user: UserSearchItem) => {
        onSelect(user);
        setOpen(false);
    };

    const buttonLabel = value ? creatorLabel({ ...value, id: value.userId }) : t("attribution.creatorEmpty");

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    id={id}
                    type="button"
                    variant="outline"
                    disabled={disabled}
                    className="w-full justify-between font-normal hover:bg-secondary-container hover:text-text-primary"
                >
                    <span className="truncate">{buttonLabel}</span>
                    <ChevronsUpDown className="size-4 shrink-0 opacity-50" aria-hidden />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                disablePortal
                className="w-[var(--radix-popover-trigger-width)] p-2"
                align="start"
            >
                <Input
                    autoFocus
                    value={input.value}
                    onChange={(e) => input.onChange(e.target.value)}
                    placeholder={t("attribution.searchPlaceholder")}
                    aria-label={t("attribution.searchPlaceholder")}
                />
                <div className="mt-2 max-h-64 overflow-y-auto" onScroll={handleScroll}>
                    {query.isPending ? (
                        <div className="flex items-center justify-center py-6 text-muted-foreground">
                            <Loader2 className="size-4 animate-spin" aria-hidden />
                        </div>
                    ) : users.length === 0 ? (
                        <p className="py-6 text-center text-sm text-muted-foreground">
                            {t("attribution.searchEmpty")}
                        </p>
                    ) : (
                        <ul className="flex flex-col gap-1">
                            {users.map((user) => {
                                const label = creatorLabel(user);
                                const selected = value?.userId === user.id;
                                return (
                                    <li key={user.id}>
                                        <button
                                            type="button"
                                            onClick={() => pick(user)}
                                            className="flex w-full cursor-pointer items-center gap-2 rounded-sm p-2 text-left hover:bg-accent hover:text-accent-foreground"
                                        >
                                            <Avatar className="size-7 shrink-0 border">
                                                <AvatarImage src={user.image ?? undefined} />
                                                <AvatarFallback className="text-[9px]">
                                                    {initials(label) || (
                                                        <UserIcon className="size-3" aria-hidden />
                                                    )}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span className="flex min-w-0 flex-1 flex-col">
                                                <span className="truncate text-sm">{label}</span>
                                                {user.email && user.email !== label ? (
                                                    <span className="truncate text-[10px] text-muted-foreground">
                                                        {user.email}
                                                    </span>
                                                ) : null}
                                            </span>
                                            {selected ? (
                                                <Check className="size-4 shrink-0" aria-hidden />
                                            ) : null}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {query.isFetchingNextPage ? (
                        <div className="flex items-center justify-center py-2 text-muted-foreground">
                            <Loader2 className="size-4 animate-spin" aria-hidden />
                        </div>
                    ) : null}
                </div>
            </PopoverContent>
        </Popover>
    );
}
